import { useState, useEffect, useRef, useCallback } from 'react';
import { Coins, Wallet, Timer, CreditCard, ChevronDown, Check, TrendingUp } from 'lucide-react';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { useDisconnect } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { EthPayment } from './payments/EthPayment';
import { BnbPayment } from './payments/BnbPayment';
import { UsdtPayment } from './payments/UsdtPayment';
import { UsdcPayment } from './payments/UsdcPayment';
import { SolPayment } from './payments/SolPayment';
import { SplTokenPayment } from './payments/SplTokenPayment';
import { CardPayment } from './payments/CardPayment';
import { defaultReferral, usdtAddress, usdcAddress } from '../config/contract';
import { useGgapPrice } from '../hooks/useGgapPrice';
import { TOKEN_MINTS, TOKEN_DECIMALS, createConnection } from '../lib/solana';
import { useOraclePrices } from '../hooks/useOraclePrices';
import SolanaWalletModal, { useWalletModal } from './SolanaWalletModal';
import { useNetworkSwitch } from '../hooks/useNetworkSwitch';
import { formatUnits } from 'viem';
import { useBalance, useAccount } from 'wagmi';
import TransactionPopup, { useTransactionPopup } from './TransactionPopup';
import { useTotalRaised } from '../hooks/useTotalRaised';
import { useUserStats } from '../hooks/useUserStats';
import { useConfirmationModal } from './ConfirmationModal';
import ConfirmationModal from './ConfirmationModal';
import { useReferral } from '../hooks/useReferral';
import ErrorPopup, { useErrorPopup } from './ErrorPopup';

const networks = [
  {
    id: 'eth', 
    name: 'Ethereum', 
    symbol: 'ETH', 
    icon: '/icons/eth.svg',
    tokens: [
      { symbol: 'ETH', name: 'Ethereum', icon: '/icons/eth.svg' },
      { symbol: 'USDT', name: 'Tether USD', icon: '/icons/usdt.svg' },
      { symbol: 'USDC', name: 'USD Coin', icon: '/icons/usdc.svg' }
    ]
  },
  {
    id: 'bsc', 
    name: 'BSC', 
    symbol: 'BNB', 
    icon: '/icons/bnb.svg',
    tokens: [
      { symbol: 'BNB', name: 'BNB', icon: '/icons/bnb.svg' },
      { symbol: 'USDT', name: 'Tether USD', icon: '/icons/usdt.svg' },
      { symbol: 'USDC', name: 'USD Coin', icon: '/icons/usdc.svg' }
    ]
  },
  {
    id: 'sol', 
    name: 'Solana', 
    symbol: 'SOL', 
    icon: '/icons/sol.svg',
    tokens: [
      { symbol: 'SOL', name: 'Solana', icon: '/icons/sol.svg' },
      { symbol: 'USDT', name: 'Tether USD', icon: '/icons/usdt.svg' },
      { symbol: 'USDC', name: 'USD Coin', icon: '/icons/usdc.svg' }
    ]
  }
];

const paymentMethods = [
  { id: 'crypto', name: 'Crypto', icon: <img src="/icons/crypto.svg" alt="Crypto" className="w-4 h-4"/> },
  { id: 'card', name: 'Card', icon: <img src="/icons/card.svg" alt="Card" className="w-4 h-4"/> }
];

const PRESALE_END_DATE = new Date('2025-06-30T15:00:00Z');
const MAX_PURCHASE_AMOUNT = 50000; // USD cap for native tokens

export default function PresaleWidget() {
  const [selectedNetwork, setSelectedNetwork] = useState(networks[0]);
  const [selectedToken, setSelectedToken] = useState(networks[0].tokens[0]);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'crypto'|'card'>('crypto');
  const [timeLeft, setTimeLeft] = useState({ days:0,hours:0,minutes:0,seconds:0 });
  const [processingBuy, setProcessingBuy] = useState(false);
  const [processingBuyAndStake, setProcessingBuyAndStake] = useState(false);
  const [error, setError] = useState('');
  
  // Add forced refresh counter to force re-renders of components
  const [forcedRefresh, setForcedRefresh] = useState(0);
  
  // Get confirmation modal hook
  const confirmationModal = useConfirmationModal();
  
  // Get error popup hook
  const errorPopup = useErrorPopup();
  
  // Dropdown states
  const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  
  // Refs for click-outside detection
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const tokenDropdownRef = useRef<HTMLDivElement>(null);
  const paymentDropdownRef = useRef<HTMLDivElement>(null);

  const { address: evmAddress, isConnected: isEvmConnected } = useAppKitAccount();
  const { publicKey: solanaAddress, connected: isSolanaConnected } = useWallet();
  const { open } = useAppKit();
  const { setOpen: openSolanaModal } = useWalletModal();
  const { priceUSD, loading: priceLoading } = useGgapPrice(selectedNetwork.id as 'eth' | 'bsc' | 'sol');
  const prices = useOraclePrices(['ethereum','binancecoin','solana']);
  const { switchToNetwork } = useNetworkSwitch();

  // Track the active wallet connection type
  const [activeWalletType, setActiveWalletType] = useState<'evm'|'sol'|'none'>('none');

  // 1 = ETH mainnet, 56 = BSC mainnet
  const chainId = selectedNetwork.id === 'eth'
    ? 1
    : selectedNetwork.id === 'bsc'
      ? 56
      : undefined;

  // Fetch balances for the selected network
  const { data: nativeBalance } = useBalance({
    address: evmAddress as `0x${string}`,
    chainId,
  });

  // Determine token addresses for USDT/USDC on EVM
  const usdtToken = selectedNetwork.id === 'sol'
    ? TOKEN_MINTS.USDT
    : usdtAddress[selectedNetwork.id as 'eth'|'bsc'] as `0x${string}`;
  const usdcToken = selectedNetwork.id === 'sol'
    ? TOKEN_MINTS.USDC
    : usdcAddress[selectedNetwork.id as 'eth'|'bsc'] as `0x${string}`;

  const { data: usdtBalanceData } = useBalance({
    address: evmAddress as `0x${string}`,
    token: selectedNetwork.id !== 'sol' ? usdtToken as `0x${string}` : undefined,
    chainId,
  });
  
  const { data: usdcBalanceData } = useBalance({
    address: evmAddress as `0x${string}`,
    token: selectedNetwork.id !== 'sol' ? usdcToken as `0x${string}` : undefined,
    chainId,
  });

  const { totalRaised, hardcap, percentage, loading: loadingRaised, evmTotal, solanaTotal, bscTotal, truesolanaTotal } = useTotalRaised();
  const { tokensBought, tokensStaked, pendingTokens, loading: loadingStats, refreshStats } = useUserStats();
  
  // Fetch Solana balance
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [splUsdtBalance, setSplUsdtBalance] = useState<number | null>(null);
  const [splUsdcBalance, setSplUsdcBalance] = useState<number | null>(null);

  // Add reference to track previous chainId to avoid unnecessary UI updates
  const previousChainIdRef = useRef<number | undefined>();

  // Add a new ref to track network change source
  const networkChangeSourceRef = useRef<'wallet' | 'ui' | null>(null);

  // Get Solana balance when SOL network is selected and wallet is connected
  useEffect(() => {
    async function getSolanaBalance() {
      if (selectedNetwork.id === 'sol' && solanaAddress && isSolanaConnected) {
        try {
          // Use Helius RPC with fallbacks through createConnection()
          const connection = createConnection();
          const balance = await connection.getBalance(solanaAddress);
          const balanceInSOL = balance / LAMPORTS_PER_SOL;
          console.log(`Solana balance: ${balanceInSOL} SOL`);
          setSolBalance(balanceInSOL);
          
          // Get SPL token balances
          try {
            // Funkcija patikrinti SPL token balansams
            const getTokenBalance = async (mintAddress: string, setter: (value: number | null) => void) => {
              try {
                const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
                  solanaAddress,
                  { programId: new PublicKey(TOKEN_PROGRAM_ID) }
                );
                
                // Ieškome įrašo su reikiamu mint adresu
                const tokenAccount = tokenAccounts.value.find(
                  account => account.account.data.parsed?.info?.mint === mintAddress
                );
                
                if (tokenAccount) {
                  const balance = tokenAccount.account.data.parsed?.info?.tokenAmount?.uiAmount || 0;
                  setter(balance);
                } else {
                  setter(0);
                }
              } catch (error) {
                // Silent fail
                setter(0);
              }
            };
            
            // Gaukime USDT ir USDC balansus
            await getTokenBalance(TOKEN_MINTS.USDT, setSplUsdtBalance);
            await getTokenBalance(TOKEN_MINTS.USDC, setSplUsdcBalance);
          } catch (splError) {
            // Silent fail
            setSplUsdtBalance(0);
            setSplUsdcBalance(0);
          }
          
        } catch (error) {
          console.error('Error fetching Solana balance:', error);
          setSolBalance(null);
          setSplUsdtBalance(0);
          setSplUsdcBalance(0);
        }
      } else {
        setSolBalance(null);
        setSplUsdtBalance(0);
        setSplUsdcBalance(0);
      }
    }
    
    getSolanaBalance();
  }, [selectedNetwork.id, solanaAddress, isSolanaConnected]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (networkDropdownRef.current && !networkDropdownRef.current.contains(event.target as Node)) {
        setNetworkDropdownOpen(false);
      }
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target as Node)) {
        setTokenDropdownOpen(false);
      }
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target as Node)) {
        setPaymentDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSuccess = useCallback((hash:string) => {
    setAmount('');
    setProcessingBuy(false);
    setProcessingBuyAndStake(false);
    
    // Force a component refresh to update the UI
    setForcedRefresh(prev => prev + 1);
    
    // Refresh user token stats after successful transaction
    if (typeof refreshStats === 'function') {
      console.log('Refreshing user stats after successful transaction');
      
      // Initial refresh
      refreshStats();
      
      // Add a delayed second refresh to ensure blockchain data has settled
      setTimeout(() => {
        console.log('Performing delayed refresh of user stats to ensure data accuracy');
        refreshStats();
        // Force another UI refresh after the delayed refresh
        setForcedRefresh(prev => prev + 1);
      }, 5000); // 5 second delay for blockchain data to settle
    }
  }, [refreshStats]);

  const handleError = (msg:string) => {
    // Show error in popup instead of inline
    errorPopup.setError(msg);
    setProcessingBuy(false);
    setProcessingBuyAndStake(false);
  };

  // Move showTransaction before it's used
  const { showTransaction } = useTransactionPopup();
  
  // Create wrapper function with compatible types for UsdcPayment
  const showTransactionForUsdc = (
    type: string, 
    message: string, 
    network: string, 
    hash: string,
    tokensReceived?: string,
    isApproval?: boolean,
    onBuyClick?: (() => void) | null
  ) => {
    showTransaction(
      type as any, 
      message, 
      network as any, 
      hash,
      tokensReceived,
      isApproval,
      onBuyClick
    );
  };

  const { referrerAddress, getReferrerAddressForChain } = useReferral();
  
  // Use referrerAddress if available, otherwise use defaultReferral
  const effectiveReferralAddress = referrerAddress || defaultReferral;

  // Get referrer addresses for each chain
  const evmReferrerAddress = getReferrerAddressForChain('evm') || defaultReferral;
  const solReferrerAddress = getReferrerAddressForChain('sol') || defaultReferral;

  // Instantiate all payment hooks
  const ethPayment       = EthPayment({   amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:false, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const ethStakePayment  = EthPayment({   amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:true,  onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake });
  const bnbPayment       = BnbPayment({   amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:false, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const bnbStakePayment  = BnbPayment({   amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:true,  onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake });
  const solPayment       = SolPayment({   amount, userAddress:solanaAddress?.toBase58(), referralAddress:solReferrerAddress, stake:false, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const solStakePayment  = SolPayment({   amount, userAddress:solanaAddress?.toBase58(), referralAddress:solReferrerAddress, stake:true,  onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake });
  const solUsdtPayment   = SplTokenPayment({ amount, userAddress:solanaAddress?.toBase58(), referralAddress:solReferrerAddress, stake:false, tokenMint:TOKEN_MINTS.USDT, decimals:TOKEN_DECIMALS.USDT, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const solUsdcPayment   = SplTokenPayment({ amount, userAddress:solanaAddress?.toBase58(), referralAddress:solReferrerAddress, stake:false, tokenMint:TOKEN_MINTS.USDC, decimals:TOKEN_DECIMALS.USDC, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const usdtPayment      = UsdtPayment({  amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:false, network:selectedNetwork.id as 'eth'|'bsc', onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const usdtStakePayment = UsdtPayment({  amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:true,  network:selectedNetwork.id as 'eth'|'bsc', onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake });
  const usdcPayment      = UsdcPayment({  amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:false, network:selectedNetwork.id as 'eth'|'bsc', onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy, showTransaction: showTransactionForUsdc });
  const usdcStakePayment = UsdcPayment({  amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:true,  network:selectedNetwork.id as 'eth'|'bsc', onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake, showTransaction: showTransactionForUsdc });
  const cardPayment      = CardPayment({ amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:false, onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuy });
  const cardStakePayment = CardPayment({ amount, userAddress:evmAddress, referralAddress:evmReferrerAddress, stake:true,  onError:handleError, onSuccess:handleSuccess, onProcessing:setProcessingBuyAndStake });

  // Return the right "max" based on token
  const getMaxAmount = () => {
    // For SOL token on Solana network
    if (selectedNetwork.id === 'sol' && selectedToken.symbol === 'SOL') {
      if (solBalance === null) return '0';
      
      // Calculate max amount in USD
      let usdVal = 0;
      if (prices?.solana) {
        usdVal = solBalance * prices.solana.usd;
      }
      
      // Cap to maximum purchase amount
      const capped = Math.min(usdVal, MAX_PURCHASE_AMOUNT);
      
      // Convert back to SOL
      if (prices?.solana && prices.solana.usd > 0) {
        return (capped / prices.solana.usd).toFixed(6);
      }
      
      return '0';
    }
    
    // For Solana SPL tokens (USDT/USDC)
    if (selectedNetwork.id === 'sol' && selectedToken.symbol === 'USDT') {
      return splUsdtBalance !== null ? splUsdtBalance.toString() : '0';
    }
    if (selectedNetwork.id === 'sol' && selectedToken.symbol === 'USDC') {
      return splUsdcBalance !== null ? splUsdcBalance.toString() : '0';
    }
    
    // For EVM tokens (original implementation)
    if (selectedToken.symbol === 'USDT') return usdtBalanceData?.formatted ?? '0';
    if (selectedToken.symbol === 'USDC') return usdcBalanceData?.formatted ?? '0';
    if (!nativeBalance) return '0';
    
    const bal = Number(formatUnits(nativeBalance.value, nativeBalance.decimals));
    let usdVal = 0;
    
    // Use oracle prices for cryptocurrency value calculation
    if (selectedToken.symbol === 'ETH' && prices?.ethereum) {
      usdVal = bal * prices.ethereum.usd;
    } else if (selectedToken.symbol === 'BNB' && prices?.binancecoin) {
      usdVal = bal * prices.binancecoin.usd;
    }
    
    const capped = Math.min(usdVal, MAX_PURCHASE_AMOUNT);
    
    if (selectedToken.symbol === 'ETH' && prices?.ethereum && prices.ethereum.usd > 0) {
      return (capped / prices.ethereum.usd).toFixed(6);
    } else if (selectedToken.symbol === 'BNB' && prices?.binancecoin && prices.binancecoin.usd > 0) {
      return (capped / prices.binancecoin.usd).toFixed(6);
    }
    
    return '0';
  };

  const handleSetMaxAmount = () => {
    setAmount(getMaxAmount());
    setError('');
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const diff = PRESALE_END_DATE.getTime() - now;
      setTimeLeft({
        days:   Math.floor(diff / (1000*60*60*24)),
        hours:  Math.floor((diff % (1000*60*60*24)) / (1000*60*60)),
        minutes:Math.floor((diff % (1000*60*60)) / (1000*60)),
        seconds:Math.floor((diff % (1000*60)) / 1000),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // reset token selector when network changes
    setSelectedToken(selectedNetwork.tokens[0]);
  }, [selectedNetwork]);

  const calculateTokens = () => {
    if (!amount || isNaN(Number(amount)) || !priceUSD) return '0';
    
    let usdAmt = Number(amount);
    
    // Use oracle prices for cryptocurrency conversions
    if (paymentMethod === 'crypto') {
      if (selectedToken.symbol === 'ETH' && prices?.ethereum) {
        usdAmt *= prices.ethereum.usd;
      }
      else if (selectedToken.symbol === 'BNB' && prices?.binancecoin) {
        usdAmt *= prices.binancecoin.usd;
      }
      else if (selectedToken.symbol === 'SOL' && prices?.solana) {
        usdAmt *= prices.solana.usd;
      }
      // For USDT and USDC, the amount is already in USD
    }
    
    return Math.floor(usdAmt / priceUSD).toLocaleString();
  };

  const handleNetworkSelect = async (net: typeof networks[0]) => {
    try {
      if (net.id === 'eth' || net.id === 'bsc') {
        // Notify user we're switching networks
        const targetNetwork = net.id === 'eth' ? 'Ethereum' : 'Binance Smart Chain';
        console.log(`Attempting to switch to ${targetNetwork}...`);
        
        try {
          await switchToNetwork(net.id);
          console.log(`Successfully switched to ${targetNetwork}`);
        } catch (switchError) {
          console.error(`Failed to switch to ${targetNetwork}:`, switchError);
          setError(`Failed to switch to ${targetNetwork}. Please switch manually in your wallet.`);
          // We still update the UI to match user selection, but inform about the mismatch
        }
      }
      
      // Update UI regardless of whether network switching succeeded
      setSelectedNetwork(net);
      setNetworkDropdownOpen(false);
      
      // Also update the token selection to the first available token on this network
      if (net.tokens.length > 0) {
        setSelectedToken(net.tokens[0]);
      }
    } catch (error) {
      console.error('Network selection error:', error);
      setError('Failed to change network. Please try again or switch manually in your wallet.');
    }
  };

  const handleTokenSelect = (token: typeof selectedNetwork.tokens[0]) => {
    setSelectedToken(token);
    setTokenDropdownOpen(false);
  };

  const handlePaymentMethodSelect = (method: 'crypto' | 'card') => {
    setPaymentMethod(method);
    
    // If switching to card payment and currently on Solana network, 
    // automatically switch to Ethereum since card only works with ETH
    if (method === 'card' && selectedNetwork.id === 'sol') {
      setSelectedNetwork(networks[0]); // Switch to Ethereum (first network)
      setSelectedToken(networks[0].tokens[0]); // Switch to ETH token
    }
    
    setPaymentDropdownOpen(false);
  };

  const handleBuy = async (stake = false) => {
    setError('');
    
    // For card payments, always use Ethereum regardless of what network was previously selected
    if (paymentMethod === 'card') {
      // If not connected to Ethereum wallet, show error
      if (activeWalletType !== 'evm') {
        setError('Please connect an Ethereum wallet for card payments');
        return;
      }
      
      // Show confirmation modal ONLY for card payments
      confirmationModal.setOpen(true, stake, 
        // onConfirm callback
        async () => {
          await (stake ? cardStakePayment.handlePayment() : cardPayment.handlePayment());
        },
        // onCancel callback
        () => {
          // Do nothing on cancel
        }
      );
    } else {
      // Regular crypto payments based on selected network and token
      if (selectedNetwork.id === 'eth' || selectedNetwork.id === 'bsc') {
        // Check if EVM wallet is connected
        if (activeWalletType !== 'evm') {
          setError(`Please connect an Ethereum wallet for ${selectedNetwork.name} payments`);
          return;
        }
        
        if (selectedNetwork.id === 'eth') {
          if (selectedToken.symbol === 'ETH')    await (stake ? ethStakePayment.handlePayment()  : ethPayment.handlePayment());
          if (selectedToken.symbol === 'USDT')   await (stake ? usdtStakePayment.handlePayment() : usdtPayment.handlePayment());
          if (selectedToken.symbol === 'USDC')   await (stake ? usdcStakePayment.handlePayment() : usdcPayment.handlePayment());
        } else { // BSC
          if (selectedToken.symbol === 'BNB')    await (stake ? bnbStakePayment.handlePayment()  : bnbPayment.handlePayment());
          if (selectedToken.symbol === 'USDT')   await (stake ? usdtStakePayment.handlePayment() : usdtPayment.handlePayment());
          if (selectedToken.symbol === 'USDC')   await (stake ? usdcStakePayment.handlePayment() : usdcPayment.handlePayment());
        }
      } else if (selectedNetwork.id === 'sol') {
        // Check if Solana wallet is connected
        if (activeWalletType !== 'sol') {
          setError('Please connect a Solana wallet for SOL payments');
          return;
        }
        
        if (selectedToken.symbol === 'SOL')    await (stake ? solStakePayment.handlePayment()  : solPayment.handlePayment());
        if (selectedToken.symbol === 'USDT')   await solUsdtPayment.handlePayment();
        if (selectedToken.symbol === 'USDC')   await solUsdcPayment.handlePayment();
      }
    }
  };

  const handleConnect = () => {
    // If using card payment, always connect to Ethereum wallet
    if (paymentMethod === 'card') {
      // Force Ethereum network for card payments
      if (selectedNetwork.id !== 'eth') {
        setSelectedNetwork(networks[0]); // Switch to Ethereum
        setSelectedToken(networks[0].tokens[0]); // Switch to ETH token
      }
      // Open Ethereum wallet connection
      open();
    } else {
      // Connect to the selected network's wallet
      if (selectedNetwork.id === 'sol') {
        // If EVM wallet is connected and trying to use Solana
        if (activeWalletType === 'evm') {
          // Let user know they need to connect Solana wallet
          setError('Please connect a Solana wallet for SOL payments');
        }
        openSolanaModal(true);
      } else {
        // If Solana wallet is connected and trying to use EVM
        if (activeWalletType === 'sol') {
          // Let user know they need to connect EVM wallet
          setError(`Please connect an Ethereum wallet for ${selectedNetwork.name} payments`);
        }
        
        // Before opening the wallet connection modal, ensure we're on the correct network
        try {
          // Try to switch to the selected network first
          if (selectedNetwork.id === 'bsc') {
            // For BSC, first switch network then open wallet connection
            switchToNetwork('bsc')
              .then(() => {
                console.log('Successfully switched to BSC network');
                open();
              })
              .catch(error => {
                console.error('Failed to switch to BSC:', error);
                setError('Failed to switch to BSC. Please try again or switch manually in your wallet.');
                // Still open wallet dialog even if network switch fails
                open();
              });
          } else {
            // For ETH, just open wallet connection
            open();
          }
        } catch (error) {
          console.error('Error during network switching:', error);
          // Fallback to simply opening the connection
          open();
        }
      }
    }
  };

  // Add imports if needed
  const { chain } = useAccount();
  
  // Update the active wallet type whenever connection status changes
  useEffect(() => {
    if (isSolanaConnected) {
      setActiveWalletType('sol');
      
      // Automatically switch to Solana network when Solana wallet is connected
      // Only switch if we're not already on Solana to prevent infinite loops
      if (selectedNetwork.id !== 'sol') {
        // Set the source of the change to prevent the other effect from firing
        networkChangeSourceRef.current = 'wallet';
        
        const solanaNetwork = networks.find(n => n.id === 'sol');
        if (solanaNetwork) {
          setSelectedNetwork(solanaNetwork);
        }
        
        // Clear the source after the state update
        setTimeout(() => {
          networkChangeSourceRef.current = null;
        }, 100);
      }
    } else if (isEvmConnected) {
      setActiveWalletType('evm');
      // Network updates now handled in a separate useEffect
    } else {
      setActiveWalletType('none');
    }
  }, [isSolanaConnected, isEvmConnected, networks]);
  
  // Improved chain detection - automatically update the UI when chain changes in MetaMask
  useEffect(() => {
    // Only run this effect if the network change wasn't initiated by the wallet connection effect
    if (isEvmConnected && chain && networkChangeSourceRef.current !== 'wallet') {
      // Map chain ID to our network type
      let networkType = 'eth'; // Default to ETH
      
      if (chain.id === 56) {
        networkType = 'bsc';
      } else if (chain.id === 1) {
        networkType = 'eth';
      }
      
      // Only update if there's a change
      if (networkType !== selectedNetwork.id) {
        console.log(`Wallet network changed to ${networkType} (chainId: ${chain.id}), updating UI from ${selectedNetwork.id}`);
        
        // Set the source of the change to prevent the other effect from firing
        networkChangeSourceRef.current = 'ui';
        
        // Find the matching network in our networks array
        const newNetwork = networks.find(n => n.id === networkType) || networks[0];
        
        // Update the selected network in UI
        setSelectedNetwork(newNetwork);
        
        // Also update the selected token to the first token in the new network
        setSelectedToken(newNetwork.tokens[0]);
        
        // Clear any network-related errors
        setError('');
        
        // Clear the source after the state update
        setTimeout(() => {
          networkChangeSourceRef.current = null;
        }, 100);
      }
    }
  }, [chain, isEvmConnected, selectedNetwork.id, networks]);
  
  // Determine proper connection status based on both payment method and selected network
  const isConnected = paymentMethod === 'card' 
    ? isEvmConnected // For card, only Ethereum connection matters
    : selectedNetwork.id === 'sol'
      ? isSolanaConnected
      : isEvmConnected;

  return (
    <>
      <div className="quantum-card p-6 w-full max-w-md">
        <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-6">
          Buy $QGEN Presale Now!
        </h3>
        
        {/* Connection status indicator - display based on the actual connected wallet */}
        {(isEvmConnected || isSolanaConnected) && (
          <div className="mb-4 py-2 px-3 rounded-lg bg-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-sm text-quantum-light">
                {activeWalletType === 'sol' ? 'Solana' : 'Ethereum'} Wallet Connected
              </span>
            </div>
            {/* Show warning if payment method doesn't match wallet type */}
            {(paymentMethod === 'card' && activeWalletType === 'sol') && (
              <div className="text-xs text-quantum-primary">Connect Ethereum wallet for card payments</div>
            )}
            {(paymentMethod === 'crypto' && selectedNetwork.id === 'sol' && activeWalletType === 'evm') && (
              <div className="text-xs text-quantum-primary">Connect Solana wallet for SOL payments</div>
            )}
            {(paymentMethod === 'crypto' && selectedNetwork.id !== 'sol' && activeWalletType === 'sol') && (
              <div className="text-xs text-quantum-primary">Connect Ethereum wallet for {selectedNetwork.name} payments</div>
            )}
            {/* Show warning when there's network mismatch between wallet and UI */}
            {activeWalletType === 'evm' && isEvmConnected && chainId && 
             ((chainId === 56 && selectedNetwork.id === 'eth') || (chainId === 1 && selectedNetwork.id === 'bsc')) && (
              <div className="text-xs text-quantum-primary">
                Network mismatch! Your wallet is on {chainId === 1 ? 'Ethereum' : 'BSC'} but UI shows {selectedNetwork.id === 'eth' ? 'Ethereum' : 'BSC'}
              </div>
            )}
          </div>
        )}
        
        {/* User Quantum Tokens - Only show if connected (moved here from below) */}
        {isConnected && (
          <div className="quantum-card p-4 mb-4" key={`user-tokens-${tokensBought}-${tokensStaked}-${forcedRefresh}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-quantum-light/70 flex items-center font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                <Coins className="w-4 h-4 mr-2 text-quantum-primary" /> Your Quantum Tokens
              </span>
              {/* Add refresh button */}
              <button 
                onClick={() => {
                  if (typeof refreshStats === 'function') {
                    console.log('Manual refresh requested by user');
                    refreshStats();
                    setForcedRefresh(prev => prev + 1);
                  }
                }} 
                className="text-xs text-quantum-primary hover:text-quantum-secondary flex items-center"
                aria-label="Refresh token balance"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            {loadingStats ? (
              <div className="flex justify-center items-center py-2">
                <span className="text-quantum-light/50">Loading...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <span className="text-xs text-quantum-light/50">Tokens Bought</span>
                  <span className="text-quantum-light font-bold">
                    {parseFloat(tokensBought || '0').toLocaleString(undefined, {maximumFractionDigits: 2})} QGEN
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-xs text-quantum-light/50 text-right">Tokens Staked</span>
                  <span className="text-quantum-light font-bold text-right">
                    {parseFloat(tokensStaked || '0').toLocaleString(undefined, {maximumFractionDigits: 2})} QGEN
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="space-y-4">
          {/* Price Card with Total Raised Progress */}
          <div className="quantum-card p-4 mb-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-quantum-light/70 font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent" style={{ fontFamily: "'Orbitron', sans-serif" }}>Token Price</span>
                <span className="text-quantum-light font-bold">${priceLoading ? '...' : priceUSD.toFixed(6)}</span>
              </div>
              {loadingRaised ? (
                <div className="w-full h-10 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full w-0 bg-gradient-to-r from-quantum-primary to-quantum-secondary" />
                </div>
              ) : (
                <>
                  <div className="w-full h-10 bg-white/5 rounded-full overflow-hidden relative">
                    <div 
                      className="h-full bg-gradient-to-r from-quantum-primary to-quantum-secondary" 
                      style={{ width: `${percentage}%` }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-sm text-white font-medium px-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                      <div className="flex flex-wrap justify-center items-center gap-x-1">
                        <span>${totalRaised !== null 
                          ? totalRaised.toLocaleString(undefined, {maximumFractionDigits: 2}) 
                          : '0'} raised</span>
                        <span>of ${hardcap.toLocaleString()}</span>
                        <span>({percentage.toFixed(1)}%)</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="text-xs text-quantum-light/50 text-right">

              </div>
            </div>
          </div>
          
          {/* Payment Options Row */}
          <div className="flex items-center justify-between gap-2 mb-4">
            {/* Payment Method Dropdown */}
            <div className="relative" ref={paymentDropdownRef}>
              <button 
                onClick={() => setPaymentDropdownOpen(!paymentDropdownOpen)}
                className={`quantum-card p-3 rounded-lg flex items-center justify-between ${paymentMethod === 'card' ? 'w-[100px]' : 'w-full'} hover:bg-white/10 transition-colors`}
              >
                <div className="flex items-center gap-2">
                  {paymentMethod === 'crypto' 
                    ? <img src="/icons/crypto.svg" alt="Crypto" className="w-4 h-4"/> 
                    : <img src="/icons/card.svg" alt="Card" className="w-4 h-4"/>}
                  <span>{paymentMethod === 'crypto' ? 'Crypto' : 'Card'}</span>
                </div>
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              
              {paymentDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full z-10 quantum-card p-1 rounded-lg">
                  {paymentMethods.map(method => (
                    <button
                      key={method.id}
                      onClick={() => handlePaymentMethodSelect(method.id as 'crypto' | 'card')}
                      className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${
                        paymentMethod === method.id 
                          ? 'bg-gradient-to-r from-quantum-primary to-quantum-secondary text-quantum-dark' 
                          : 'hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {method.icon}
                        <span>{method.name}</span>
                      </div>
                      {paymentMethod === method.id && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {paymentMethod === 'crypto' && (
              <>
                {/* Network Dropdown */}
                <div className="relative" ref={networkDropdownRef}>
                  <button 
                    onClick={() => setNetworkDropdownOpen(!networkDropdownOpen)}
                    className={`quantum-card p-3 rounded-lg flex items-center justify-between w-full hover:bg-white/10 transition-colors ${
                      (activeWalletType === 'evm' && chainId === 56) ? 'border border-yellow-500/50' : 
                      (activeWalletType === 'evm' && chainId === 1) ? 'border border-blue-500/50' :
                      (activeWalletType === 'sol') ? 'border border-green-500/50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <img src={selectedNetwork.icon} alt={selectedNetwork.name} className="w-5 h-5" />
                      <span>{selectedNetwork.name}</span>
                      {/* Show a badge if wallet network differs from selected UI network */}
                      {activeWalletType === 'evm' && chainId && (
                        (chainId === 56 && selectedNetwork.id === 'eth') ? 
                          <span className="text-xs px-1 py-0.5 bg-yellow-500/30 text-yellow-300 rounded">BSC wallet</span> :
                        (chainId === 1 && selectedNetwork.id === 'bsc') ?
                          <span className="text-xs px-1 py-0.5 bg-blue-500/30 text-blue-300 rounded">ETH wallet</span> :
                        null
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </button>
                  
                  {networkDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full z-10 quantum-card p-1 rounded-lg">
                      {networks.map(network => {
                        // Determine if this network is the one connected in the wallet
                        const isActiveInWallet = (network.id === 'eth' && chainId === 1) || 
                                                (network.id === 'bsc' && chainId === 56) ||
                                                (network.id === 'sol' && activeWalletType === 'sol');
                                                    
                        return (
                          <button
                            key={network.id}
                            onClick={() => handleNetworkSelect(network)}
                            className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${
                              selectedNetwork.id === network.id 
                                ? 'bg-gradient-to-r from-quantum-primary to-quantum-secondary text-quantum-dark' 
                                : isActiveInWallet
                                  ? 'bg-white/10 border border-green-500/50'
                                  : 'hover:bg-white/10'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <img src={network.icon} alt={network.name} className="w-5 h-5" />
                              <span>{network.name}</span>
                              {isActiveInWallet && network.id !== selectedNetwork.id && (
                                <span className="text-xs text-green-300">(connected)</span>
                              )}
                            </div>
                            {selectedNetwork.id === network.id && <Check className="w-4 h-4" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Token Dropdown */}
                <div className="relative" ref={tokenDropdownRef}>
                  <button 
                    onClick={() => setTokenDropdownOpen(!tokenDropdownOpen)}
                    className="quantum-card p-3 rounded-lg flex items-center justify-between w-full hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <img src={selectedToken.icon} alt={selectedToken.symbol} className="w-5 h-5" />
                      <span>{selectedToken.symbol}</span>
                    </div>
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </button>
                  
                  {tokenDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 w-full z-10 quantum-card p-1 rounded-lg">
                      {selectedNetwork.tokens.map(token => (
                        <button
                          key={token.symbol}
                          onClick={() => handleTokenSelect(token)}
                          className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${
                            selectedToken.symbol === token.symbol 
                              ? 'bg-gradient-to-r from-quantum-primary to-quantum-secondary text-quantum-dark' 
                              : 'hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <img src={token.icon} alt={token.symbol} className="w-5 h-5" />
                            <span>{token.symbol}</span>
                          </div>
                          {selectedToken.symbol === token.symbol && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Card payment option */}
            {paymentMethod === 'card' && (
              <div className="quantum-card p-3 rounded-lg flex items-center justify-center flex-1">
                <div className="flex items-center gap-2">
                  <img src="/icons/card.svg" alt="Card" className="w-4 h-4" />
                  <span className="text-quantum-light/70 text-sm">USD Payment</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Amount Input + MAX */}
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={e=>{
                const v=e.target.value;
                if(v===''||/^\d*\.?\d*$/.test(v)){ setAmount(v); setError(''); }
              }}
              placeholder={paymentMethod==='card'?'Amount in USD':`Amount in ${selectedToken.symbol}`}
              className="quantum-input w-full pr-32 transition-all duration-300 focus:scale-[1.02]"
            />
            {paymentMethod==='crypto' && ['ETH','BNB','SOL','USDT','USDC'].includes(selectedToken.symbol) && (
              <button onClick={handleSetMaxAmount} className="absolute right-24 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-quantum-primary/20 text-quantum-primary rounded hover:bg-quantum-primary/30 transition-colors">
                MAX
              </button>
            )}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-quantum-primary">
              {calculateTokens()} QGEN
            </div>
          </div>
          
          {!isConnected ? (
            <button onClick={handleConnect} className="quantum-button w-full flex items-center justify-center space-x-2 py-3">
              {paymentMethod === 'card' ? (
                <img src="/icons/card.svg" alt="Card" className="w-5 h-5"/> 
              ) : selectedNetwork.id === 'sol' ? (
                <img src="/icons/sol.svg" alt="Solana" className="w-5 h-5"/> 
              ) : selectedNetwork.id === 'bsc' ? (
                <img src="/icons/bnb.svg" alt="BSC" className="w-5 h-5"/> 
              ) : (
                <img src="/icons/eth.svg" alt="Ethereum" className="w-5 h-5"/> 
              )}
              <span>
                {paymentMethod === 'card' 
                  ? 'Connect Wallet' 
                  : `Connect ${selectedNetwork.name} Wallet`}
              </span>
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={()=>handleBuy(false)} disabled={processingBuy||processingBuyAndStake} className="quantum-button py-2 px-4 flex-1 flex items-center justify-center gap-2">
                <Coins className="w-4 h-4"/>{processingBuy?'Processing...':'Buy'}
              </button>
              <button onClick={()=>handleBuy(true)} disabled={processingBuy||processingBuyAndStake} className="quantum-button py-2 px-4 flex-1 flex items-center justify-center gap-2">
                <Coins className="w-4 h-4"/>{processingBuyAndStake?'Processing...':'Buy & Stake'}
              </button>
            </div>
          )}
          <div className="text-center">
            <a href="https://codearmory.io" target="_blank" rel="noopener noreferrer" className="text-xs text-quantum-light/50 hover:text-quantum-primary transition-colors duration-300">
              Widget Powered By CodeArmory
            </a>
          </div>
        </div>
      </div>
      <SolanaWalletModal />
      <ConfirmationModal />
      <TransactionPopup />
      <ErrorPopup />
    </>
  );
}
